import "../helpers/publicPath";
import React, { useReducer } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { theme } from "../theme";
import { NavView } from "../features/nav/NavView";
import { RenderKind } from "../types/render-kind";
import { CartProvider } from "../features/cart";

import { ProductSliderCollection } from "../modules/ProductSliderCollection";
import { initSentry } from "../helpers/errors";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../helpers/fetch";
import { Customer, CustomerProvider } from "../features/CustomerContext";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { initializeAnnouncementBar } from "../modules/announcement-bar";
import { trimPortalProps } from "../helpers/droppsApp";
import { growthBook } from "../helpers/growthbook";
export { setCartAndCheckout } from "../services/shopifyCart";
export { getUTMAttributes } from "../helpers/utm";
export { generateRechargeUrl } from "../features/portal/RechargeApiActions";
export { getProduct } from "../helpers/droppsApp";

initSentry();
initializeAnnouncementBar();

export const renders: ({ id: string } & RenderKind)[] = [];

const PortalComponent: React.FC<RenderKind> = (render) => {
  switch (render.kind) {
    case "nav-view":
      return <NavView {...trimPortalProps(render)} />;
    case "product-slider":
      return <ProductSliderCollection {...trimPortalProps(render)} />;
    default:
      return null;
  }
};

const DroppsApp = () => {
  // Whenever someone pushes a new render, force react to re-render
  const [count, forceUpdate] = useReducer((x) => x + 1, 0);
  if (count === 0) {
    renders.push = (...args) => {
      const ret = Array.prototype.push.call(renders, ...args);
      forceUpdate();
      return ret;
    };
  }

  const mobile: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.lg)
  );

  return (
    <>
      {renders.map((x) => {
        const el = document.getElementById(x.id);
        if (!el) {
          throw new Error(`expected id ${x.id} to exist`);
        }
        const key = `${x.id}-${x.kind}`;
        return createPortal(
          <Sentry.ErrorBoundary showDialog>
            <PortalComponent {...x} />
          </Sentry.ErrorBoundary>,
          el,
          key
        );
      })}
    </>
  );
};

export const render = ({ user }: { user: Customer | null }) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
    });
  }

  const container = document.getElementById("dropps-app-root");
  if (container === null) {
    throw new Error("dropps-app-root not found");
  }

  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <GrowthBookProvider growthbook={growthBook}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <CustomerProvider customer={user}>
                <CartProvider>
                  <DroppsApp />
                </CartProvider>
              </CustomerProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </GrowthBookProvider>
    </React.StrictMode>
  );
};
